"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
// import axios from "axios"; // only needed by the disabled campaign API call below
import { handleCampaignID } from "@/lib/utils";
import { googleTagManager } from "@/components/common/utils";
import { buildWaLink } from "@/lib/ctw/waLink";
import { onCtwCode } from "@/lib/ctw/mintClient";

const DEFAULT_PHONE_NUMBER = "+916366267773";
// const CAMPAIGN_API_URL =
//   "https://mfx-prod-154625965269.asia-south1.run.app/campaign/tk/"; // only needed by the disabled campaign API call below
const DEFAULT_TOKEN_ID = "e3a268cf-420c-415c-a234-2825d878e688";

interface CampaignResponse {
  id: number;
  whatsapp_phone_number: string;
  whatsapp_message: string;
  [key: string]: any;
}

const stripZeroWidth = (str: string) => (str ? str.replace(/[​‌‍]/g, "") : "");

export const getWAChatEvent = (pathname: string): string => {
  if (pathname.startsWith("/cpa-cma")) return "CPA_CMA_LP_F_WAChatWithUs";
  if (pathname === "/ea") return "EA_LP_F_WAChatWithUs";
  if (pathname.startsWith("/cma")) return "CMA_LP_F_WAChatWithUs";
  return "CPA_LP_F_WAChatWithUs";
};

export const getDefaultMessage = (pathname: string): string => {
  if (pathname.startsWith("/cpa-cma")) {
    return "Hi, I'm interested in your AI-integrated accounting programs. Could you share more details?";
  }
  if (pathname === "/" || pathname.startsWith("/cpa") || pathname === "/ea") {
    return "Hi, I'm interested in the AI-integrated CPA program. Could you share more details?";
  }
  if (pathname.startsWith("/cma")) {
    return "Hi, I'm interested in the AI-integrated CMA program. Could you share more details?";
  }
  return "Hi, I'm interested in the AI-integrated CPA program. Could you share more details?";
};

// Campaign API is currently not in use — using the static default
// message/phone number instead. Uncomment fetchWhatsappCampaignDetails'
// body below (and the CAMPAIGN_API_URL constant and axios import above)
// to re-enable.
const fetchWhatsappCampaignDetails = async (
  _tokenId: string | null,
): Promise<CampaignResponse | null> => {
  return null;

  /*
  if (!tokenId) return null;

  try {
    const response = await axios.get(`${CAMPAIGN_API_URL}${tokenId}`, {
      timeout: 10000,
      headers: { "Content-Type": "application/json" },
    });

    const result = response.data?.result ?? null;

    if (result) {
      sessionStorage.setItem("WA_MSG_GEN_TOKEN", JSON.stringify(result));
    }

    return result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMsg = error.response
        ? `Returns Status: ${error.response.status}`
        : error.request
          ? "Timeout or network error"
          : error.message;
      console.error(`❌ Campaign API Error: ${errorMsg}`);
    } else {
      console.error("❌ Unexpected error fetching campaign details:", error);
    }
    return null;
  }
  */
};

interface UseWhatsappLinkOptions {
  skip?: boolean;
  buttonLocation?: string;
  /**
   * Overrides the pathname-derived copy for callers that need their own
   * wording. A campaign message from the API still wins over this — the
   * override only replaces the fallback.
   */
  defaultMessage?: string;
}

interface UseWhatsappLinkResult {
  waLink: string;
  phoneNumber: string;
  handleWhatsAppClick: () => void;
}

export const useWhatsappLink = ({
  skip = false,
  buttonLocation = "floating_whatsapp",
  defaultMessage,
}: UseWhatsappLinkOptions = {}): UseWhatsappLinkResult => {
  const pathname = usePathname();
  const [message, setMessage] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  useEffect(() => {
    if (skip) return;

    let isMounted = true;

    const initializeWidget = async () => {
      const dynamicTokenId =
        (typeof window !== "undefined" && handleCampaignID()) ||
        DEFAULT_TOKEN_ID;

      const remoteData = await fetchWhatsappCampaignDetails(dynamicTokenId);

      if (!isMounted) return;

      const resolvedPhoneNumber =
        remoteData?.whatsapp_phone_number ?? DEFAULT_PHONE_NUMBER;
      setPhoneNumber(resolvedPhoneNumber);

      let finalMessage = "";
      if (remoteData?.whatsapp_message?.trim()) {
        finalMessage = stripZeroWidth(remoteData.whatsapp_message).trim();
      }

      if (!finalMessage) {
        finalMessage = defaultMessage?.trim() || getDefaultMessage(pathname);
      }

      setMessage(finalMessage);
    };

    initializeWidget();

    return () => {
      isMounted = false;
    };
  }, [skip]);

  // rebuild the link when a CTW source code arrives after the initial render
  const [ctwTick, setCtwTick] = useState(0);
  useEffect(() => onCtwCode(() => setCtwTick((t) => t + 1)), []);

  const waLink = useMemo(() => {
    if (!phoneNumber) return "";
    const msgToEncode =
      message || defaultMessage?.trim() || getDefaultMessage(pathname);
    return buildWaLink(phoneNumber, msgToEncode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, message, pathname, defaultMessage, ctwTick]);

  const handleWhatsAppClick = () => {
    googleTagManager(getWAChatEvent(pathname), {
      button_location: buttonLocation,
      page_url: typeof window !== "undefined" ? window.location.href : "",
    });
  };

  return { waLink, phoneNumber, handleWhatsAppClick };
};
